import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, CircularProgress, Grid } from '@material-ui/core';
import { DataTable } from '../../../contexts/dataTable';
import { getUsers } from '../../../actions/user.actions';
import { setLoading } from '../../../actions/system.actions';
import AddUserPage from './add/AddUsers';
import UsersSearchForm from '../../../components/UsersSearch/UsersSearchForm';
import StatusUserDropdown from '../../../components/StatusDropdown/StatusUserDropdown';
import DeleteUser from './delete/DeleteUser';
import axios from 'axios';

const sortableColumns = [
  'userName',
  'email',
  'phone_number',
  'user_role',
  'status',
];

const defaultPagination = {
  page: 1,
  page_size: 10
};

const defaultSearchParams = {
  username: '',
  email: '',
  phone_number: '',
  status: '',
  user_role: '',
};

const UsersPage = () => {
  const [searchParams, setSearchParams] = useState(defaultSearchParams);
  const [pagination, setPagination] = useState(defaultPagination);
  const [sort, setSort] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  const dispatch = useDispatch();
  const cancelSearch = useRef(null);

  const { users, paging } = useSelector((state) => state.listUsersReducer);

  const columns = useMemo(
    () => [
      {
        Header: 'User Name',
        accessor: 'userName',
        Cell({ row }) {
          const { first_name, last_name } = row.original;
          return `${first_name} ${last_name}`;
        },
      },
      {
        Header: 'Email',
        accessor: 'email',
      },
      {
        Header: 'Phone number',
        accessor: 'phone_number',
      },
      {
        Header: 'Role',
        accessor: 'user_role',
        Cell({ value }) {
          return <Typography variant="p">{value}</Typography>;
        },
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell({ row }) {
          return (
            <StatusUserDropdown
              id={row.original.id}
              stautsChip={row.original.status}
            />
          );
        },
      },
      {
        Header: '',
        id: 'actions',
        Cell({ row }) {
          return <DeleteUser userId={row.original.id} />;
        },
      },
    ],
    []
  );

  const handleClearSearch = useCallback(() => {
    setSearchParams(defaultSearchParams);
    setHasSearched(false);
    // Reset pagination when clearing search
    setPagination(defaultPagination);
  }, []);

  const handleSearch = useCallback(() => {
    setHasSearched(true);
    // Reset pagination when performing new search
    setPagination(defaultPagination);
    
    // Manually trigger API call when search button is clicked
    if (cancelSearch.current) {
      cancelSearch.current.cancel();
    }
    cancelSearch.current = axios.CancelToken.source();

    const apiParams = {
      page: 1, // Reset to first page for new search
      page_size: defaultPagination.page_size,
      ...sort,
    };

    // Add search parameters if they have values
    if (searchParams.username && searchParams.username.trim()) {
      apiParams.username = searchParams.username.trim();
    }
    if (searchParams.email && searchParams.email.trim()) {
      apiParams.email = searchParams.email.trim();
    }
    if (searchParams.phone_number && searchParams.phone_number.trim()) {
      apiParams.phone_number = searchParams.phone_number.trim();
    }
    if (searchParams.status && searchParams.status.trim()) {
      apiParams.status = searchParams.status.trim();
    }
    if (searchParams.user_role && searchParams.user_role.trim()) {
      apiParams.user_role = searchParams.user_role.trim();
    }

    setIsLoading(true);
    dispatch(getUsers(apiParams, cancelSearch.current.token)).catch((error) => {
      if (!axios.isCancel(error)) {
        throw new Error(error);
      }
    }).finally(() => setIsLoading(false));
  }, [searchParams, sort]);

  const getData = useCallback(() => {
    if (cancelSearch.current) {
      cancelSearch.current.cancel();
    }
    cancelSearch.current = axios.CancelToken.source();

    // Build API parameters based on search fields
    const apiParams = {
      ...pagination,
      ...sort,
    };

    // Only add search parameters if they have values and a search has been performed
    if (hasSearched) {
      if (searchParams.username && searchParams.username.trim()) {
        apiParams.username = searchParams.username.trim();
      }
      if (searchParams.email && searchParams.email.trim()) {
        apiParams.email = searchParams.email.trim();
      }
      if (searchParams.phone_number && searchParams.phone_number.trim()) {
        apiParams.phone_number = searchParams.phone_number.trim();
      }
      if (searchParams.status && searchParams.status.trim()) {
        apiParams.status = searchParams.status.trim();
      }
      if (searchParams.user_role && searchParams.user_role.trim()) {
        apiParams.user_role = searchParams.user_role.trim();
      }
    }

    setIsLoading(true);
    dispatch(getUsers(apiParams, cancelSearch.current.token)).catch((error) => {
      if (!axios.isCancel(error)) {
        throw new Error(error);
      }
    }).finally(() => setIsLoading(false));
  }, [hasSearched, sort, pagination]);

  // Initial load - load all users without search filters
  useEffect(() => {
    if (!hasSearched) {
      getData();
    }
  }, [getData, hasSearched]);

  // Load data when pagination or sorting changes (only for searched results)
  useEffect(() => {
    if (hasSearched && (pagination.page > 1 || Object.keys(sort).length > 0)) {
      getData();
    }
  }, [getData, hasSearched, pagination, sort]);

  const handleSuccess = useCallback(() => {
    handleClearSearch();
    getData();
  }, [handleClearSearch, getData]);

  const isSearchActive = hasSearched && Object.values(searchParams).some(value => value && value.trim());

  return (
    <Box mr={4} pb={4}>
      <Grid container justify="space-between" alignItems="flex-start">
        <Grid item xs={12}>
          <UsersSearchForm
            searchParams={searchParams}
            onSearchParamsChange={setSearchParams}
            onSearch={handleSearch}
            onClear={handleClearSearch}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>
      <Grid container justify="flex-end" style={{ marginBottom: 20 }}>
        <Grid item>
          <AddUserPage handleSuccess={handleSuccess} />
        </Grid>
      </Grid>
      {isLoading ? (
        <Box
          display='flex'
          justifyContent='center'
          mt={2}
          alignItems='center'
          height='50vh'
        >
          <CircularProgress color='primary' />
        </Box>
      ) : (
        <DataTable 
          data={users || []} 
          columns={columns} 
          title="Users" 
          handlePaging={setPagination}
          paging={paging}
          initialState={{
            sortBy: [
              {
                id: 'userName',
                desc: false,
              }
            ],
            paginable: true
          }}
          sortingChanged={setSort}
          sort={sort}
          sortableColumns={sortableColumns}
        />
      )}
      {!users?.length && isSearchActive ? (
        <Typography variant="h5">No results</Typography>
      ) : null}
    </Box>
  );
};

export default UsersPage;